import { GET_TOKEN_URL } from '../auth.constant';
import { ITokenExtras } from '../auth.interface';

type FetchInput = Parameters<typeof fetch>[0];
type FetchInit = Parameters<typeof fetch>[1];

let originalFetch: typeof fetch | undefined;
let handler: ((extras: ITokenExtras) => void) | undefined;

const isTokenRequest = (input: FetchInput, init: FetchInit): boolean => {
  const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase();

  if (method !== 'POST') {
    return false;
  }

  const url = input instanceof Request ? input.url : input.toString();

  return url.includes(GET_TOKEN_URL);
};

const parseExtras = (body: unknown): ITokenExtras => {
  const data = (body ?? {}) as Partial<ITokenExtras>;

  return {
    excludedScope: Array.isArray(data.excludedScope) ? data.excludedScope : [],
  };
};

/**
 * Wraps the global fetch once to capture the non-OIDC top-level fields
 * (excludedScope) of the /api/v1/auth/token response, which keycloak-js
 * fetches internally and discards. The original response is returned untouched; the
 * extras are read from a cloned response before returning so they are available as
 * soon as keycloak.init() resolves. The handler is updated on every call so the latest
 * auth service instance always receives the values.
 */
export const installTokenResponseInterceptor = (onExtras: (extras: ITokenExtras) => void): void => {
  handler = onExtras;

  if (originalFetch) {
    return;
  }

  originalFetch = window.fetch.bind(window);

  window.fetch = async (input: FetchInput, init?: FetchInit): Promise<Response> => {
    const response = await originalFetch!(input, init);

    if (handler && isTokenRequest(input, init)) {
      try {
        handler(parseExtras(await response.clone().json()));
      } catch {
        // Ignore parse errors — keycloak-js handles its own token error flow.
      }
    }

    return response;
  };
};
